import { AnchorLink, ButtonLink } from "@/components/links";
import { cn, filterKeys } from "@/utils";
import { orpc } from "@/utils/clients/orpc";
import { useHasPermission } from "@/utils/permissions";
import { Button } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";
import type { AdminTrip } from "@workspace/admin-domain/src/features/trip/schema";
import dayjs from "@workspace/domain/src/utils/dayjs";
import { t } from "i18next";
import { LucidePlus } from "lucide-react";
import { useMemo } from "react";
import { z } from "zod";
import Reservations from "../-reservations/-index";
import { NewReservation } from "../-reservations/-new";
import { ReservationFiltersSchema } from "../-reservations/-utils";
import { OverviewSection } from "../analytics/-overview";
import AuditLogList from "../audit-log/-list";
import { AuditLogFiltersSchema, DEFAULT_AUDIT_LOG_PARAMS } from "../audit-log/-utils";
import NewClient from "../clients/-new";
import TripsList from "../trips/-list";
import { DEFAULT_TRIPS_PARAMS, TripFiltersSchema } from "../trips/-utils";

export const Route = createFileRoute("/_authenticated/_home/")({
  validateSearch: z.object(ReservationFiltersSchema.shape),
  component: Component,
  loaderDeps: ({ search }) => filterKeys(search, ReservationFiltersSchema.shape),
  loader: ({ context, deps }) => {
    const startDate = dayjs().subtract(1, "week").startOf("day").toDate();
    const endDate = dayjs().endOf("day").toDate();
    const auditLogFilters = filterKeys(deps, AuditLogFiltersSchema.shape);
    const tripFilters = filterKeys(deps, TripFiltersSchema.shape);

    void Promise.all([
      // Overview (analytics)
      context.queryClient.prefetchQuery(
        orpc.analytics.overview.getTotalReservations.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.finance.getTotalRevenue.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.overview.getTotalDue.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.overview.getNewClients.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      // Trips (home: upcoming only, first page)
      context.queryClient.prefetchInfiniteQuery(
        orpc.trip.getAll.infiniteOptions({
          input: (paginate) => ({
            ...DEFAULT_TRIPS_PARAMS,
            ...tripFilters,
            dateMin: startDate,
            dateMax: endDate,
            sortBy: "startDate",
            sortOrder: "asc",
            paginate,
          }),
          initialPageParam: undefined as AdminTrip["GetAll"]["paginate"],
          getNextPageParam: () => undefined as AdminTrip["GetAll"]["paginate"],
        }),
      ),
      // Audit log (home: first page, limit 14)
      context.queryClient.prefetchInfiniteQuery(
        orpc.auditLog.getAll.infiniteOptions({
          input: (page) => ({
            ...DEFAULT_AUDIT_LOG_PARAMS,
            ...auditLogFilters,
            page,
            limit: 14,
          }),
          initialPageParam: DEFAULT_AUDIT_LOG_PARAMS.page,
          getNextPageParam: (lastPage, allPages) => {
            if (lastPage.length < 14) return undefined;
            return allPages.length + 1;
          },
        }),
      ),
      // Reservations (mode "all" – filters applied client-side)
      context.queryClient.prefetchQuery(
        orpc.reservation.getAll.queryOptions({
          input: { value: null, type: "all" },
        }),
      ),
    ]);
  },
});

function Component() {
  const { startDate, endDate } = useMemo(
    () => ({
      startDate: dayjs().subtract(1, "week").startOf("day").toDate(),
      endDate: dayjs().endOf("day").toDate(),
    }),
    [],
  );

  const hasAnalyticsPermission = useHasPermission("analytics:get");

  return (
    <div className="gap-md md:gap-lg flex grow flex-col">
      <HomeHeader />

      {hasAnalyticsPermission ? <OverviewSection startDate={startDate} endDate={endDate} /> : null}
      <div className="gap-md md:gap-lg grid lg:grid-cols-6 lg:grid-rows-[auto_1fr]">
        <HomeTripsList />
        <HomeAuditLogList />
        <HomeReservationsList />
      </div>
    </div>
  );
}

function HomeHeader() {
  const { auth } = Route.useRouteContext();

  const hasTripCreatePermission = useHasPermission(true);
  const hasReservationCreatePermission = useHasPermission(true);
  const hasClientCreatePermission = useHasPermission(true);

  return (
    <div className="gap-md py-md flex flex-col md:flex-row md:items-center md:justify-between">
      <p className="text-2xl font-semibold md:text-3xl">{t("home.title", { name: auth.user.name.split(" ")[0] })}</p>
      <div className="gap-sm flex flex-col-reverse items-center md:flex-row">
        {hasTripCreatePermission ? (
          <ButtonLink to="/trips/new" leftSection={<LucidePlus />} variant="default" className="w-full md:w-auto">
            {t("trip.new")}
          </ButtonLink>
        ) : null}
        {hasReservationCreatePermission ? (
          <NewReservation
            trigger={(onClick) => (
              <Button onClick={onClick} leftSection={<LucidePlus />} variant="default" className="w-full md:w-auto">
                {t("reservation.new")}
              </Button>
            )}
          />
        ) : null}
        {hasClientCreatePermission ? (
          <NewClient
            trigger={(onClick) => (
              <Button onClick={onClick} leftSection={<LucidePlus />} variant="default" className="w-full md:w-auto">
                {t("client.new")}
              </Button>
            )}
          />
        ) : null}
      </div>
    </div>
  );
}

function HomeAuditLogList() {
  const hasPermission = useHasPermission(true);
  if (!hasPermission) return null;
  return (
    <div className="dark:bg-dark-6 border-s-primary-filled gap-md p-md flex h-full min-w-0 flex-col rounded-lg rounded-s-none border border-s-3 lg:col-span-2 lg:row-span-2">
      <div className="flex items-baseline justify-between">
        <h2 className="text-xl font-semibold">{t("auditLog.title")}</h2>
        <AnchorLink to="/audit-log" className="pe-1.5">
          {t("auditLog.viewAll")}
        </AnchorLink>
      </div>

      <div className="gap-md flex grow flex-col">
        <AuditLogList mode="home" size="sm" />
      </div>
    </div>
  );
}

function HomeTripsList() {
  const hasPermission = useHasPermission("trip:get");
  const hasAuditLogPermission = useHasPermission(true);
  if (!hasPermission) return null;
  return (
    <div
      className={cn(
        "dark:bg-dark-6 border-s-primary-filled gap-md p-md flex h-fit min-w-0 flex-col rounded-lg rounded-s-none border border-s-3",
        hasAuditLogPermission ? "lg:col-span-4" : "lg:col-span-6",
      )}
    >
      <div className="flex items-baseline justify-between">
        <h2 className="text-xl font-semibold">{t("home.trips.upcoming.title")}</h2>
        <AnchorLink to="/trips" className="pe-1.5">
          {t("auditLog.viewAll")}
        </AnchorLink>
      </div>
      <div className="gap-md flex grow flex-col">
        <TripsList home />
      </div>
    </div>
  );
}

function HomeReservationsList() {
  const hasPermission = useHasPermission("reservation:get");
  if (!hasPermission) return null;
  return <Reservations mode="all" />;
}
